import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Monitor, Server, Network, Code, Building, Cpu, Wifi, Wrench, Zap, Battery } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Monitor: Monitor,
  Server: Server,
  Network: Network,
  Code: Code,
  Building: Building,
  Cpu: Cpu,
  Wifi: Wifi,
  Wrench: Wrench,
  Zap: Zap,
  Battery: Battery
};

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 15 }
    }
  };

  return (
    <section id="services" className="services-section" ref={ref}>
      <div className="services-container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Our Services
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Comprehensive IT
            <span className="gradient-text"> Solutions</span>
          </motion.h2>
          <motion.p 
            className="section-subtitle"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            End-to-end technology services designed to power your business forward
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="services-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div 
                key={service.id} 
                className="service-card"
                variants={cardVariants}
                whileHover={{ 
                  y: -10, 
                  boxShadow: '0 25px 50px rgba(59, 130, 246, 0.15)',
                  borderColor: '#3b82f6'
                }}
                style={{ opacity: 1 }}
                data-testid={`service-card-${service.id}`}
              >
                <div className="service-card-inner">
                  <motion.div 
                    className="service-icon"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {IconComponent && <IconComponent size={32} />}
                  </motion.div>
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                  <div className="service-hover-effect"></div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          className="services-cta"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          style={{ opacity: isInView ? 1 : 0 }}
        >
          <motion.div 
            className="cta-card"
            whileHover={{ scale: 1.02 }}
          >
            <div className="cta-content">
              <h3>Need a Custom Solution?</h3>
              <p>Let's discuss your specific requirements and build the perfect IT infrastructure for your business.</p>
            </div>
            <motion.a 
              href="#contact" 
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="contact-us-btn"
            >
              <span>Contact Us</span>
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
